import { groupBy, mapKeys, mapValues, camelCase } from 'lodash'

import { isNotEmptyElement } from './array'
import { AnyObject, Collection, Dictionary } from './types/utility'

export const hasId = <T = string>(entity: object): entity is { id: T } => !!(entity as any)?.id

export const groupByUniqueIdProp = <E extends Dictionary<any, any>, K extends keyof E>(
  collection: Collection<E>,
  key: K
): Dictionary<E[K], E> => {
  const array = Array.isArray(collection) ? collection : Object.values(collection)
  const grouped = groupBy(array, key)
  return mapValues(grouped, arr => arr[0]) as any
}

export const keysToCamelCase = <In extends AnyObject, Out extends AnyObject>(input: In): Out => {
  return mapKeys(input, (_, key) => camelCase(key)) as any
}

export function isPlainObject(value: any): value is Record<string, any> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  )
}

export function deepTransformPrimitives<Val extends any, TransformedVals>(
  value: Val,
  condition: (val: any) => val is TransformedVals,
  transformer: (val: TransformedVals) => TransformedVals
): Val {
  if (value == null) {
    return value
  }

  if (condition(value)) {
    return transformer(value) as unknown as Val
  }

  if (Array.isArray(value)) {
    return value.map(item => deepTransformPrimitives(item, condition, transformer)) as Val
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        deepTransformPrimitives(v, condition, transformer)
      ])
    ) as Val
  }

  return value
}

export function isNotEmptyObject(object: AnyObject): boolean {
  return isNotEmptyElement(object) && !!Object.keys(object).length
}
